import React, { FC, useContext, useState } from 'react'
import { StyledButtonGroup } from './ChangeEmail.styled'
import { TNInput } from '../components/TNInput'
import { useDispatch, useSelector } from 'react-redux'
import { AppState, encryptPass } from '@news-mono/web-common'
import { TNButton } from '../components/TNButton'
import { useChangeEmail } from '../hooks/useChangeEmail'
import { MyAccountContext } from '../hooks/useMyAccountContext'
import { TNLink } from '../components/TNLink'
import { tokens } from '@news-mono/design-tokens'
import { metrics } from '../../__styling'
import { UserSettingProps } from '../UserSetting'
import { useForgotPassword } from '../hooks/useChangePassword'
import { BannerHelperProps } from './helpers/change-email-banner-helper'
import {
    ClickHelperProps,
    onChangeEmailFormCancelHelper,
    onChangeEmailFormSubmitHandler,
    onForgotPasswordClick,
} from './helpers/change-email-click-helper'
import { useSendVerifyEmail } from '../hooks/useSendVerifyEmail'

// Stores the current email, userId & password that gets typed into the fields.
export interface ChangeEmailFormProps {
    email: string
    userId: string
    currentPassword: string
    newEmail: string
}
// Used to store which field is erroring out
export interface ChangeEmailErrorProps {
    emailError: boolean
    passwordError: boolean
}

// static values used for margins and colors
const { palette } = tokens.thenightly.colors
const { margins } = metrics.thenightly

export interface ChangeEmailProps extends UserSettingProps {}

export const ChangeEmail: FC<ChangeEmailProps> = ({ onEvent, log, config }) => {
    // Pass through context before anything
    const myAccountContext = useContext(MyAccountContext)
    const dispatch = useDispatch()
    // Select the user email & wan user id from the current authenticated state
    const { userEmail, wanUserId } = useSelector(
        (state: AppState) => state.authentication,
    )
    // Store the values utilised to proceed with the email change
    const [emailChangeProps, setEmailChangeProps] =
        useState<ChangeEmailFormProps>({
            email: userEmail || '',
            userId: wanUserId,
            currentPassword: '',
            newEmail: userEmail || '',
        })
    // Store the submitting and error values
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [errors, setErrors] = useState<ChangeEmailErrorProps>({
        emailError: false,
        passwordError: false,
    })

    // Stores the backend handlers below
    const { mutateAsync: sendForgotPasswordEmail } = useForgotPassword({
        email: userEmail!,
    })
    const { mutateAsync: sendEmailChangeUpdate } =
        useChangeEmail(emailChangeProps)
    const { mutateAsync: sendVerifyEmail } = useSendVerifyEmail(wanUserId)

    // Stores all external helper props below
    const bannerProps: BannerHelperProps = {
        myAccountContext,
        errorState: { errors, setErrors },
        dispatch,
        log,
        config,
        onEvent,
    }
    const clickProps: ClickHelperProps = {
        myAccountContext,
        mutateProps: {
            sendEmailChangeUpdate,
            sendForgotPasswordEmail,
            sendVerifyEmail,
        },
        emailChangeState: {
            emailChangeProps,
            setEmailChangeProps,
        },
        submittingState: {
            submitting,
            setSubmitting,
        },
    }

    // Builds the component
    return (
        <form autoComplete={'off'}>
            <TNInput
                id="email"
                type="edit"
                label="Email Address"
                value={userEmail}
                hasError={errors.emailError}
                onChange={(text) => {
                    setEmailChangeProps({
                        ...emailChangeProps,
                        newEmail: text,
                    })
                }}
                onSelect={() => setErrors({ ...errors, emailError: false })}
            />
            <TNInput
                id="password"
                type="password"
                label="Enter Password"
                hasError={errors.passwordError}
                onChange={async (text) => {
                    const encryptedPass =
                        (await encryptPass(text, config.passEncryptionKey)) ||
                        ''
                    setEmailChangeProps({
                        ...emailChangeProps,
                        currentPassword: encryptedPass,
                    })
                }}
                onSelect={() => setErrors({ ...errors, passwordError: false })}
                onEnter={(e) =>
                    onChangeEmailFormSubmitHandler(clickProps, bannerProps, e)
                }
                marginBottom={margins.md}
            />
            <TNLink
                color={palette.section.opinion.default}
                onClick={() => onForgotPasswordClick(clickProps, bannerProps)}
                display={'flex'}
                width={'fit-content'}
                marginBottom={margins.lg}
            >
                Forgot your password?
            </TNLink>
            <StyledButtonGroup>
                <TNButton
                    onClick={(e) =>
                        onChangeEmailFormCancelHelper(clickProps, e)
                    }
                    btnType="muted"
                >
                    CANCEL
                </TNButton>
                <TNButton
                    onClick={(e) =>
                        onChangeEmailFormSubmitHandler(
                            clickProps,
                            bannerProps,
                            e,
                        )
                    }
                    btnType="primary"
                    disabled={submitting}
                >
                    SAVE
                </TNButton>
            </StyledButtonGroup>
        </form>
    )
}
